/**

 * Bundle Quantity Break — "Buy X, Get X Free"

 * ---------------------------------------------------------------

 * Every tier is Buy N Get N Free. Slot 0 is always the host

 * (locked, paid). Slots 1..(N-1) are extra PAID picks. Slots

 * N..(2N-1) are FREE picks. Total slots per tier = 2N.

 *

 * Any slot the shopper hasn't manually chosen is auto-filled with

 * a duplicate of the host product (autoFillEmptySlotsForTier), so

 * Add to Cart works immediately without forcing a "Choose" click

 * first. Clicking "Change" on any slot still lets them swap it for

 * a different product from the catalog.

 *

 * No add-to-cart button of its own — intercepts the click on the

 * page's native Add to Cart button and sends ONE combined

 * /cart/add.js request (host + every slot item, all tagged with

 * shared _bundle_id / _bundle_tier properties), then hands the

 * response to mini-cart's own renderContents(), matching how this

 * theme's native <product-form> already works.

 *

 * Product rows inside the modal are fetched client-side from

 * /collections/{handle}/products.json with cursor pagination.

 *

 * Pricing note: price shown here is for display only. To make

 * checkout actually charge correctly, use a Shopify Function keyed

 * off the _bundle_id line item property, or a native automatic

 * discount scoped to the eligible collection(s).

 */

 

const PRODUCTS_PER_PAGE = 50;

 

class BundleQuantityBreak extends HTMLElement {

  constructor() {

    super();

    this.productId = this.dataset.productId;

    this.basePrice = parseInt(this.dataset.basePrice, 10);

    this.baseCompareAt = parseInt(this.dataset.baseCompareAt, 10);

    this.currency = this.dataset.currency;

    this.tierDiscounts = this.dataset.tierDiscounts

      .split(',')

      .map((n) => parseInt(n, 10));

 

    this.selectedTier = 1;

    this.slots = {};

    this.activeSlotContext = null;

    this.collectionState = {};

    this.isSubmitting = false;

  }

 

  connectedCallback() {

    this.tierInputs = [...this.querySelectorAll('.bundle-qty-break__tier-radio')];

    this.incompleteHint = this.querySelector('[data-incomplete-hint]');

    this.modal = this.querySelector('[data-modal]');

    this.modalBody = this.querySelector('[data-modal-body]');

    this.modalSearch = this.querySelector('[data-modal-search]');

    this.noResultsEl = this.querySelector('[data-no-results]');

    this.tabButtons = [...this.querySelectorAll('[data-tab-target]')];

    this.tabPanels = [...this.querySelectorAll('.bundle-qty-break__tab-panel')];

 

    if (this.modal && this.modal.parentElement !== document.body) {

      document.body.appendChild(this.modal);

    }

 

    this.bindTierSelection();

    this.bindModalOpenClose();

    this.bindTabs();

    this.bindSearch();

    this.bindInfiniteScroll();

    this.bindNativeAddToCart();

 

    this.revealSlotsForSelectedTier();

    this.autoFillEmptySlotsForTier(this.selectedTier);

    this.updateUI();

 

    const firstPanel = this.tabPanels[0];

    if (firstPanel) this.loadMoreForPanel(firstPanel);

  }

 

  /* ---------------------------------------------------------- tiers */

 

  bindTierSelection() {

    this.tierInputs.forEach((input) => {

      input.addEventListener('change', () => {

        this.selectedTier = parseInt(input.value, 10);

        this.querySelectorAll('.bundle-qty-break__tier').forEach((el) => {

          el.classList.toggle(

            'is-selected',

            parseInt(el.dataset.tier, 10) === this.selectedTier

          );

        });

        this.revealSlotsForSelectedTier();

        this.autoFillEmptySlotsForTier(this.selectedTier);

        this.updateUI();

      });

    });

  }

 

  revealSlotsForSelectedTier() {

    this.querySelectorAll('[data-slots-for-tier]').forEach((slotsEl) => {

      const tier = parseInt(slotsEl.dataset.slotsForTier, 10);

      slotsEl.hidden = tier !== this.selectedTier;

    });

  }

 

  /**

   * Fills any slot the shopper hasn't manually chosen with a

   * duplicate of the host product, so every tier is add-to-cart

   * ready the instant it's selected — no forced "Choose" click.

   * Never overwrites a slot the shopper already picked themselves.

   */

  autoFillEmptySlotsForTier(tier) {

    const totalSlots = this.getSlotCountForTier(tier);

    if (totalSlots <= 1) return;

 

    const hostSlotEl = this.querySelector(

      `[data-slots-for-tier="${tier}"] [data-slot-index="0"]`

    );

    const hostImg = hostSlotEl ? hostSlotEl.querySelector('img') : null;

    const hostName = hostSlotEl ? hostSlotEl.querySelector('.bundle-qty-break__slot-name') : null;

 

    const hostItem = {

      productId: this.productId,

      variantId: this.dataset.defaultVariantId,

      title: hostName ? hostName.textContent : '',

      price: this.basePrice,

      compareAtPrice: this.baseCompareAt,

      image: hostImg ? hostImg.getAttribute('src') : '',

    };

 

    this.slots[tier] = this.slots[tier] || {};

    for (let i = 1; i < totalSlots; i += 1) {

      if (this.slots[tier][i]) continue; // don't clobber a manual choice

      this.slots[tier][i] = hostItem;

      this.renderSlot(tier, i, hostItem);

    }

  }

 

  /* ---------------------------------------------------------- modal */

 

  bindModalOpenClose() {

    this.querySelectorAll('[data-open-modal]').forEach((btn) => {

      btn.addEventListener('click', () => {

        this.activeSlotContext = {

          tier: parseInt(btn.dataset.tier, 10),

          slotIndex: parseInt(btn.dataset.slotIndex, 10),

        };

        this.openModal();

      });

    });

 

    this.modal.querySelectorAll('[data-modal-close]').forEach((el) => {

      el.addEventListener('click', () => this.closeModal());

    });

 

    const overlay = this.modal.querySelector('.bundle-qty-break__modal-overlay');

    if (overlay) {

      overlay.addEventListener('click', (e) => {

        if (e.target === overlay) this.closeModal();

      });

    }

 

    document.addEventListener('keydown', (e) => {

      if (e.key === 'Escape' && !this.modal.hidden) this.closeModal();

    });

 

    this.modal.addEventListener('click', (e) => {

      if (e.target.hasAttribute('data-choose-product')) {

        this.handleChooseProduct(e.target);

      }

    });

 

    this.modal.addEventListener('change', (e) => {

      if (e.target.hasAttribute('data-variant-option-select')) {

        this.updateRowPriceForSelectedOptions(e.target.closest('[data-product-row]'));

      }

    });

  }

 

  openModal() {

    this.modal.hidden = false;

    document.body.style.overflow = 'hidden';

    if (this.modalSearch) {

      this.modalSearch.value = '';

      this.filterProductRows('');

      requestAnimationFrame(() => this.modalSearch.focus());

    }

  }

 

  closeModal() {

    this.modal.hidden = true;

    document.body.style.overflow = '';

    this.activeSlotContext = null;

  }

 

  /* ---------------------------------------------------------- tabs */

 

  bindTabs() {

    this.tabButtons.forEach((btn) => {

      btn.addEventListener('click', () => {

        this.tabButtons.forEach((b) => {

          const active = b === btn;

          b.classList.toggle('is-active', active);

          b.setAttribute('aria-selected', active ? 'true' : 'false');

        });

 

        let newlyActivePanel = null;

        this.tabPanels.forEach((panel) => {

          const isActive = panel.id === btn.dataset.tabTarget;

          panel.classList.toggle('is-active', isActive);

          if (isActive) newlyActivePanel = panel;

        });

 

        if (this.modalSearch) this.modalSearch.value = '';

        if (newlyActivePanel) {

          if (newlyActivePanel.dataset.loaded === 'true') {

            this.filterProductRows('');

          } else if (this.noResultsEl) {

            this.noResultsEl.hidden = true;

          }

 

          if (newlyActivePanel.dataset.loaded !== 'true') {

            this.loadMoreForPanel(newlyActivePanel);

          }

        }

      });

    });

  }

 

  /* ---------------------------------------------------------- product fetching (infinite scroll) */

 

  bindInfiniteScroll() {

    this.tabPanels.forEach((panel) => {

      const sentinel = panel.querySelector('[data-scroll-sentinel]');

      if (!sentinel) return;

 

      const observer = new IntersectionObserver(

        (entries) => {

          entries.forEach((entry) => {

            if (entry.isIntersecting) this.loadMoreForPanel(panel);

          });

        },

        { root: this.modalBody, rootMargin: '200px' }

      );

      observer.observe(sentinel);

    });

  }

 

  async loadMoreForPanel(panel) {

    const handle = panel.dataset.collectionHandle;

    const state =

      this.collectionState[handle] ||

      (this.collectionState[handle] = {

        page: 1,

        hasMore: true,

        isLoading: false,

        products: [],

      });

 

    if (state.isLoading || !state.hasMore) return;

    state.isLoading = true;

 

    const loadingIndicator = panel.querySelector('[data-loading-indicator]');

    if (loadingIndicator) loadingIndicator.hidden = false;

 

    try {

      const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=${PRODUCTS_PER_PAGE}&page=${state.page}`;

      const response = await fetch(url);

      if (!response.ok) throw new Error(`Failed to load collection "${handle}" (page ${state.page}).`);

 

      const data = await response.json();

      const products = Array.isArray(data.products) ? data.products : [];

 

      if (products.length === 0) {

        state.hasMore = false;

      } else {

        state.products.push(...products);

        this.renderProductRows(panel, products);

        state.page += 1;

        if (products.length < PRODUCTS_PER_PAGE) state.hasMore = false;

      }

 

      panel.dataset.loaded = 'true';

      this.filterProductRows(this.modalSearch ? this.modalSearch.value : '');

    } catch (err) {

      console.error('[bundle-quantity-break]', err);

    } finally {

      state.isLoading = false;

      if (loadingIndicator) loadingIndicator.hidden = true;

    }

  }

 

  renderProductRows(panel, products) {

    const list = panel.querySelector('[data-product-list]');

    if (!list) return;

 

    const fragment = document.createDocumentFragment();

 

    products.forEach((p) => {

      // Keep the current product visible so customers can choose the same

      // product again as one of their free bundle items.

 

      const row = document.createElement('div');

      row.className = 'bundle-qty-break__product-row';

      row.dataset.productRow = '';

      row.dataset.productTitle = p.title;

 

      const image = p.images && p.images[0] ? p.images[0].src : '';

 

      const variants = p.variants || [];

      let defaultVariant =

        variants.find((v) =>

          [v.option1, v.option2, v.option3].some(

            (opt) => typeof opt === 'string' && opt.trim().toLowerCase() === '100ml'

          )

        ) || variants.find((v) => v.available) || variants[0];

 

      const priceNow = defaultVariant ? parseFloat(defaultVariant.price) : 0;

      const compareAt = defaultVariant ? parseFloat(defaultVariant.compare_at_price) : 0;

 

      const priceHtml =

        compareAt > priceNow

          ? `<span class="bundle-qty-break__price-now" data-row-price-now>${this.formatMoney(priceNow * 100)}</span>

             <span class="bundle-qty-break__price-compare" data-row-price-compare>${this.formatMoney(compareAt * 100)}</span>`

          : `<span class="bundle-qty-break__price-now" data-row-price-now>${this.formatMoney(priceNow * 100)}</span>`;

 

      const hasOptions =

        p.options &&

        p.options.length &&

        !(p.options.length === 1 && p.options[0].name === 'Title');

 

      let optionsHtml = '';

      if (hasOptions) {

        optionsHtml = '<span class="bundle-qty-break__product-variant-picker">';

        p.options.forEach((option, idx) => {

          const optionName = option.name;

          const values = [...new Set(variants.map((v) => v[`option${idx + 1}`]))];

          const defaultValueForThisOption = defaultVariant ? defaultVariant[`option${idx + 1}`] : null;

          optionsHtml += `

            <label class="bundle-qty-break__variant-label">

              ${this.escapeHtml(optionName)}

              <select data-variant-option-select data-option-position="${idx + 1}">

                ${values

                  .map(

                    (val) =>

                      `<option value="${this.escapeHtml(val)}"${val === defaultValueForThisOption ? ' selected' : ''}>${this.escapeHtml(val)}</option>`

                  )

                  .join('')}

              </select>

            </label>`;

        });

        optionsHtml += '</span>';

      }

 

      row.innerHTML = `

        <span class="bundle-qty-break__product-thumb">

          ${image ? `<img src="${image}" loading="lazy" width="64" height="64" alt="${this.escapeHtml(p.title)}">` : ''}

        </span>

        <span class="bundle-qty-break__product-info">

          <span class="bundle-qty-break__product-name">${this.escapeHtml(p.title)}</span>

          <span class="bundle-qty-break__product-price">${priceHtml}</span>

          ${optionsHtml}

        </span>

        <button type="button" class="bundle-qty-break__product-choose" data-choose-product data-product-id="${p.id}" data-default-variant-id="${defaultVariant ? defaultVariant.id : ''}">

          ${this.chooseButtonLabel()}

        </button>

      `;

 

      row._variants = variants.map((v) => ({

        id: v.id,

        title: v.title,

        price: Math.round(parseFloat(v.price) * 100),

        compare_at_price: v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : null,

        available: v.available,

        options: [v.option1, v.option2, v.option3].filter((o) => o !== null && o !== undefined),

        featured_image: (v.featured_image && v.featured_image.src) || image,

      }));

 

      fragment.appendChild(row);

    });

 

    list.appendChild(fragment);

  }

 

  chooseButtonLabel() {

    return (this.modal && this.modal.dataset.chooseLabel) || 'Choose';

  }

 

  escapeHtml(str) {

    const div = document.createElement('div');

    div.textContent = str ?? '';

    return div.innerHTML;

  }

 

  /* ---------------------------------------------------------- search */

 

  bindSearch() {

    if (!this.modalSearch) return;

    this.modalSearch.addEventListener('input', () => {

      this.filterProductRows(this.modalSearch.value);

    });

  }

 

  filterProductRows(query) {

    const normalized = query.trim().toLowerCase();

    const activePanel = this.tabPanels.find((p) => p.classList.contains('is-active'));

    if (!activePanel) return;

 

    const rows = [...activePanel.querySelectorAll('[data-product-row]')];

    let visibleCount = 0;

 

    rows.forEach((row) => {

      const title = (row.dataset.productTitle || '').toLowerCase();

      const matches = normalized === '' || title.includes(normalized);

      row.hidden = !matches;

      if (matches) visibleCount += 1;

    });

 

    if (this.noResultsEl) this.noResultsEl.hidden = visibleCount !== 0;

  }

 

  /* ---------------------------------------------------------- choose product */

 

  resolveSelectedVariant(row, defaultVariantId) {

    const variants = row._variants || [];

    let resolved = variants.find((v) => String(v.id) === String(defaultVariantId));

 

    const optionSelects = [...row.querySelectorAll('[data-variant-option-select]')];

    if (optionSelects.length) {

      const chosenOptions = optionSelects

        .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)

        .map((sel) => sel.value);

 

      const match = variants.find((v) =>

        v.options.every((opt, idx) => opt === chosenOptions[idx])

      );

      if (match) resolved = match;

    }

 

    return resolved;

  }

 

  updateRowPriceForSelectedOptions(row) {

    if (!row) return;

    const chooseBtn = row.querySelector('[data-choose-product]');

    if (!chooseBtn) return;

 

    const variant = this.resolveSelectedVariant(row, chooseBtn.dataset.defaultVariantId);

    if (!variant) return;

 

    const priceNowEl = row.querySelector('[data-row-price-now]');

    const priceCompareEl = row.querySelector('[data-row-price-compare]');

    const priceNow = variant.price;

    const compareAt = variant.compare_at_price;

 

    if (priceNowEl) priceNowEl.textContent = this.formatMoney(priceNow);

 

    if (compareAt && compareAt > priceNow) {

      if (priceCompareEl) {

        priceCompareEl.textContent = this.formatMoney(compareAt);

        priceCompareEl.hidden = false;

      } else if (priceNowEl) {

        const span = document.createElement('span');

        span.className = 'bundle-qty-break__price-compare';

        span.dataset.rowPriceCompare = '';

        span.textContent = this.formatMoney(compareAt);

        priceNowEl.insertAdjacentElement('afterend', span);

      }

    } else if (priceCompareEl) {

      priceCompareEl.hidden = true;

    }

  }

 

  handleChooseProduct(btn) {

    if (!this.activeSlotContext) return;

 

    const row = btn.closest('[data-product-row]');

    const chosenVariant = this.resolveSelectedVariant(row, btn.dataset.defaultVariantId);

 

    if (!chosenVariant) {

      console.error('[bundle-quantity-break] Could not resolve a variant for this product.');

      return;

    }

 

    const { tier, slotIndex } = this.activeSlotContext;

    this.slots[tier] = this.slots[tier] || {};

    this.slots[tier][slotIndex] = {

      productId: btn.dataset.productId,

      variantId: chosenVariant.id,

      title: row.dataset.productTitle,

      price: chosenVariant.price,

      compareAtPrice: chosenVariant.compare_at_price || chosenVariant.price,

      image: chosenVariant.featured_image,

    };

 

    this.renderSlot(tier, slotIndex, this.slots[tier][slotIndex]);

    this.closeModal();

    this.updateUI();

  }

 

  renderSlot(tier, slotIndex, item) {

    const slotEl = this.querySelector(

      `[data-slots-for-tier="${tier}"] [data-slot-index="${slotIndex}"]`

    );

    if (!slotEl) return;

 

    const thumb = slotEl.querySelector('[data-slot-thumb]');

    const name = slotEl.querySelector('[data-slot-name]');

    const chooseBtn = slotEl.querySelector('.bundle-qty-break__slot-choose');

 

    if (thumb) {

      thumb.innerHTML = item.image

        ? `<img src="${item.image}" width="40" height="40" loading="lazy" alt="${this.escapeHtml(item.title)}">`

        : '';

    }

    if (name) name.textContent = item.title;

    if (chooseBtn) chooseBtn.textContent = 'Change';

    slotEl.classList.add('is-filled');

  }

 

  /* ---------------------------------------------------------- pricing / state */

 

  getSlotCountForTier(tier) {

    const slotsEl = this.querySelector(`[data-slots-for-tier="${tier}"]`);

    if (!slotsEl) return 1;

    return slotsEl.querySelectorAll('[data-slot-index]').length; // includes locked host slot

  }

 

  isTierComplete(tier) {

    // Only the FREE product slots must be selected.

    // Buy 1 Get 1: choose slot 1.

    // Buy 2 Get 2: choose slots 2 and 3.

    const slotsForTier = this.slots[tier] || {};

    const firstFreeSlot = tier;

    const lastFreeSlot = tier * 2 - 1;

 

    for (let i = firstFreeSlot; i <= lastFreeSlot; i += 1) {

      if (!slotsForTier[i] || !slotsForTier[i].variantId) return false;

    }

 

    return true;

  }

 

calculateTierTotal(tier) {
  return this.basePrice * tier;
}
 

  calculateTierCompareAtTotal(tier) {

    const totalSlots = this.getSlotCountForTier(tier);

    const slotsForTier = this.slots[tier] || {};

    let total = this.baseCompareAt;

    for (let i = 1; i < totalSlots; i += 1) {

      if (slotsForTier[i]) total += slotsForTier[i].compareAtPrice;

    }

    return total;

  }

 

  updateUI() {

    const complete = this.isTierComplete(this.selectedTier);

    if (this.incompleteHint) this.incompleteHint.hidden = complete;

    this.toggleAttribute('data-bundle-incomplete', !complete);

 

    const tierEl = this.querySelector(

      `.bundle-qty-break__tier[data-tier="${this.selectedTier}"]`

    );

    if (tierEl) {

      const priceNowEl = tierEl.querySelector('[data-price-now]');

      if (priceNowEl) {

        priceNowEl.textContent = this.formatMoney(this.calculateTierTotal(this.selectedTier));

      }

 

      const priceCompareEl = tierEl.querySelector('[data-price-compare]');

      if (priceCompareEl) {

        priceCompareEl.textContent = this.formatMoney(

          this.calculateTierCompareAtTotal(this.selectedTier)

        );

      }

    }

 

    this.querySelectorAll('.bundle-qty-break__tier[data-tier]').forEach((el) => {

      const tier = parseInt(el.dataset.tier, 10);

      const priceCompareEl = el.querySelector('[data-price-compare]');

      if (priceCompareEl) {

        priceCompareEl.textContent = this.formatMoney(this.calculateTierCompareAtTotal(tier));

      }

      const priceNowEl = el.querySelector('[data-price-now]');

      if (priceNowEl) {

        priceNowEl.textContent = this.formatMoney(this.calculateTierTotal(tier));

      }

    });

  }

 

  formatMoney(cents) {

    if (window.Shopify && window.Shopify.formatMoney) {

      return window.Shopify.formatMoney(cents, window.theme?.shopSettings?.moneyFormat || '${{amount}}');

    }

    return `$${(cents / 100).toFixed(2)}`;

  }

 

  /* ---------------------------------------------------------- native add-to-cart interception */

 

  bindNativeAddToCart() {

    const formId = this.dataset.formId;

    const form = formId ? document.getElementById(formId) : null;

 

    if (!form) {

      console.warn(

        '[bundle-quantity-break] Could not find the product form (#' + formId + ').'

      );

      return;

    }

 

    this.nativeForm = form;

    this.miniCart = document.querySelector('mini-cart');

    this.nativeSubmitButton = form.querySelector('button[type="submit"]');

 

    if (!this.nativeSubmitButton) {

      console.warn('[bundle-quantity-break] Could not find the submit button inside the product form.');

      return;

    }

 

    /*

     * IMPORTANT:

     * Intercept the FORM submit at document capture level. This runs before

     * the theme's normal product-form submit handler, so the host product

     * cannot be submitted once by the theme and again by this bundle script.

     * It also covers button clicks and Enter-key submissions.

     */

    this._bundleSubmitHandler = (event) => {

      if (event.target !== this.nativeForm) return;

 

      event.preventDefault();

      event.stopPropagation();

      event.stopImmediatePropagation();

 

      if (this.isSubmitting) return;

 

      this.submitBundle();

    };

 

    document.addEventListener('submit', this._bundleSubmitHandler, true);

  }

 

  disconnectedCallback() {

    if (this._bundleSubmitHandler) {

      document.removeEventListener('submit', this._bundleSubmitHandler, true);

    }

  }

 

  /**

   * Sends ONE combined /cart/add.js request containing the host

   * product (read live from the form's own #id input) plus every

   * slot item for the selected tier (auto-filled duplicates count

   * too — they're real cart lines, just priced at $0 via the tier

   * discount), tagged with shared _bundle_id / _bundle_tier

   * properties. Requests the theme's own cart sections back in the

   * same response so mini-cart can re-render itself, matching how

   * the theme's native <product-form> already works.

   */

  async submitBundle() {

    if (this.isSubmitting) return;

 

    const idField = this.nativeForm.querySelector('[name="id"]');

    const hostVariantId = idField ? idField.value : this.dataset.defaultVariantId;

    const tier = Number(this.selectedTier) || 1;

    const slotsForTier = this.slots[tier] || {};

 

    // Only the free slots need a customer selection.

    // The paid quantity is the main product quantity:

    // Buy 1 Get 1 = main product quantity 1 + 1 chosen free item.

    // Buy 2 Get 2 = main product quantity 2 + 2 chosen free items.

    if (!this.isTierComplete(tier)) {

      if (this.incompleteHint) {

        this.incompleteHint.hidden = false;

        this.incompleteHint.scrollIntoView({ behavior: 'smooth', block: 'center' });

      }

      return;

    }

 

    const bundleId = `bundle_${Date.now()}`;

    const sharedProperties = {

      _bundle_id: bundleId,

      _bundle_tier: tier,

    };

 

    const items = [

      {

        id: hostVariantId,

        quantity: tier,

        properties: {

          ...sharedProperties,

          _bundle_role: 'paid',

        },

      },

    ];

 

    // Add only the N FREE selections. Ignore the extra paid display slots,

    // because the paid side is represented by quantity N of the main product.

    for (let slotIndex = tier; slotIndex < tier * 2; slotIndex += 1) {

      const selectedItem = slotsForTier[slotIndex];

 

      items.push({

        id: selectedItem.variantId,

        quantity: 1,

        properties: {

          ...sharedProperties,

          _bundle_role: 'free',

          _bundle_free_slot: slotIndex - tier + 1,

        },

      });

    }

 

    const totalUnits = items.reduce((sum, item) => sum + item.quantity, 0);

    const expectedUnits = tier * 2;

 

    if (totalUnits !== expectedUnits) {

      console.error(

        `[bundle-quantity-break] Expected ${expectedUnits} total units, received ${totalUnits}.`,

        items

      );

      alert('The bundle quantity could not be prepared correctly. Please refresh and try again.');

      return;

    }

 

    this.isSubmitting = true;

 

    const submitButton = this.nativeSubmitButton;

    submitButton.classList.add('loading');

    submitButton.setAttribute('aria-disabled', 'true');

    submitButton.disabled = true;

 

    const payload = {

      items,

      sections_url: window.location.pathname,

    };

 

    if (this.miniCart && typeof this.miniCart.getSectionsToRender === 'function') {

      payload.sections = this.miniCart.getSectionsToRender().map((section) => section.id);

    }

 

    try {

      const response = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {

        method: 'POST',

        headers: {

          'Content-Type': 'application/json',

          Accept: 'application/json',

        },

        body: JSON.stringify(payload),

      });

 

      const data = await response.json();

 

      if (!response.ok || data.status) {

        throw new Error(

          data.description || data.message || 'Could not add this bundle to your cart.'

        );

      }

 

      if (this.incompleteHint) this.incompleteHint.hidden = true;

 

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {

        publish(PUB_SUB_EVENTS.cartUpdate, {

          source: 'bundle-quantity-break',

          productVariantId: hostVariantId,

          cartData: data,

        });

      }

 

      if (this.miniCart && payload.sections && typeof this.miniCart.renderContents === 'function') {

        this.miniCart.renderContents(data);

      } else {

        window.location.href = window.theme?.routes?.cart_url || '/cart';

      }

    } catch (err) {

      console.error('[bundle-quantity-break]', err);

      alert(err.message || 'Something went wrong adding this bundle to your cart.');

    } finally {

      submitButton.classList.remove('loading');

      submitButton.removeAttribute('aria-disabled');

      submitButton.disabled = false;

      this.isSubmitting = false;

    }

  }

}

 

customElements.define('bundle-quantity-break', BundleQuantityBreak);